// useStlVolume.js
// React hook to compute volume of a binary STL (or a THREE.BufferGeometry)
// No external packages required.

import { useMemo } from "react";

/** ---- Linear algebra helpers ---- */
function dot(a, b) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

/** ---- Core volume from triangles ----
 * triangles: array of [[x,y,z],[x,y,z],[x,y,z]]
 * returns { signed, absolute }
 */
export function computeVolumeFromTriangles(triangles) {
  let sum = 0.0;
  for (let i = 0; i < triangles.length; i++) {
    const [v1, v2, v3] = triangles[i];
    sum += dot(v1, cross(v2, v3)) / 6.0;
  }
  return { signed: sum, absolute: Math.abs(sum) };
}

/** ---- Parse binary STL (ArrayBuffer) to triangles ----
 * Binary STL layout:
 *   80-byte header
 *   uint32 triangle count (LE)
 *   triangle records (50 bytes each):
 *     normal (3*float32), v1 (3*float32), v2 (3*float32), v3 (3*float32), uint16 attr
 */
export function parseBinarySTLToTriangles(arrayBuffer) {
  const dv = new DataView(arrayBuffer);
  if (dv.byteLength < 84) {
    throw new Error("File too small to be a valid binary STL.");
  }
  // skip 80-byte header
  const triCount = dv.getUint32(80, true);
  const expected = 84 + triCount * 50;
  if (dv.byteLength < expected) {
    throw new Error(
      `Unexpected EOF: header says ${triCount} triangles but file is only ${dv.byteLength} bytes (expected ≥ ${expected}).`
    );
  }

  const triangles = new Array(triCount);
  let offset = 84; // start of first record
  for (let i = 0; i < triCount; i++) {
    // normal (ignored)
    // nx, ny, nz at offset..offset+12
    offset += 12;

    // v1, v2, v3 (9 floats)
    const v1 = [
      dv.getFloat32(offset + 0, true),
      dv.getFloat32(offset + 4, true),
      dv.getFloat32(offset + 8, true),
    ];
    const v2 = [
      dv.getFloat32(offset + 12, true),
      dv.getFloat32(offset + 16, true),
      dv.getFloat32(offset + 20, true),
    ];
    const v3 = [
      dv.getFloat32(offset + 24, true),
      dv.getFloat32(offset + 28, true),
      dv.getFloat32(offset + 32, true),
    ];
    triangles[i] = [v1, v2, v3];

    // advance: 9 floats (36 bytes) + attr uint16 (2 bytes)
    offset += 36 + 2;
  }
  return triangles;
}

/** ---- Compute volume from THREE.BufferGeometry ----
 * Accepts indexed or non-indexed geometry.
 */
export function computeVolumeFromGeometry(geometry) {
  // Ensure we have positions
  const posAttr = geometry.attributes?.position;
  if (!posAttr) throw new Error("geometry has no position attribute.");

  // Build triangles from indices or sequential triplets
  const positions = posAttr.array;
  const index = geometry.index ? geometry.index.array : null;

  let sum = 0.0;
  if (index) {
    for (let i = 0; i < index.length; i += 3) {
      const a = index[i] * 3,
        b = index[i + 1] * 3,
        c = index[i + 2] * 3;
      const v1 = [positions[a], positions[a + 1], positions[a + 2]];
      const v2 = [positions[b], positions[b + 1], positions[b + 2]];
      const v3 = [positions[c], positions[c + 1], positions[c + 2]];
      sum += dot(v1, cross(v2, v3)) / 6.0;
    }
  } else {
    // assume triangles in order
    for (let i = 0; i < positions.length; i += 9) {
      const v1 = [positions[i], positions[i + 1], positions[i + 2]];
      const v2 = [positions[i + 3], positions[i + 4], positions[i + 5]];
      const v3 = [positions[i + 6], positions[i + 7], positions[i + 8]];
      sum += dot(v1, cross(v2, v3)) / 6.0;
    }
  }
  return { signed: sum, absolute: Math.abs(sum) };
}

/** ---- React hook ----
 * Usage (binary STL):
 *   const { volume, signedVolume, triangleCount, error } = useStlVolume({ arrayBuffer });
 *
 * Usage (Three.js geometry):
 *   const { volume } = useStlVolume({ geometry: mesh.geometry });
 */
export default function useStlVolume({
  arrayBuffer,
  geometry,
  wantSigned = false,
}) {
  return useMemo(() => {
    try {
      if (arrayBuffer) {
        const tris = parseBinarySTLToTriangles(arrayBuffer);
        const { signed, absolute } = computeVolumeFromTriangles(tris);
        return {
          volume: wantSigned ? signed : absolute,
          signedVolume: signed,
          triangleCount: tris.length,
          error: null,
        };
      }
      if (geometry) {
        const { signed, absolute } = computeVolumeFromGeometry(geometry);
        // We can't cheaply count triangles without traversing indices again; estimate:
        const triCount = geometry.index
          ? geometry.index.count / 3
          : geometry.attributes.position.count / 3;
        return {
          volume: wantSigned ? signed : absolute,
          signedVolume: signed,
          triangleCount: triCount | 0,
          error: null,
        };
      }
      return {
        volume: null,
        signedVolume: null,
        triangleCount: 0,
        error: "No input provided.",
      };
    } catch (e) {
      return {
        volume: null,
        signedVolume: null,
        triangleCount: 0,
        error: e.message || String(e),
      };
    }
  }, [arrayBuffer, geometry, wantSigned]);
}
